import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} SoleilDiscountBadgeRefs
 * @property {HTMLElement} badgeContent
 * @property {HTMLElement} productBadges
 * @property {HTMLElement} variantBadges
 */

/**
 * Badge row above the product title. Owns two containers:
 * - [ref="productBadges"] — static list from soleil_product.badges
 * - [ref="variantBadges"] — discount pill + soleil_pack.badges for the
 *   selected variant, rebuilt from window.soleilBadgeData on variant change
 *
 * Substitution rule (per Per-Variant Badge SPEC):
 *   If the selected variant has a discount OR any soleil_pack.badges →
 *     show the variant container, hide the product container.
 *   Else if the product has soleil_product.badges →
 *     show the product container, hide the variant container.
 *   Else →
 *     collapse the wrapper entirely.
 *
 * Hidden↔visible state is now toggled instantly via the
 * `.soleil-discount-badge--hidden` class (CSS controls visibility only — no
 * transition). Custom motion was removed from this file as part of the
 * site-wide motion-strip pass.
 *
 * @extends {Component<SoleilDiscountBadgeRefs>}
 */
class SoleilDiscountBadge extends Component {
  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section');
    if (!section) return;
    section.addEventListener(ThemeEvents.variantUpdate, this.updateBadge);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section');
    if (section) {
      section.removeEventListener(ThemeEvents.variantUpdate, this.updateBadge);
    }
  }

  updateBadge = (event) => {
    const variantId = event.detail?.resource?.id;
    const productId = event.detail?.data?.productId;
    if (!variantId || !productId) return;

    // Combined listings can mount more than one badge block on a page;
    // only react to updates for our own product.
    if (String(productId) !== String(this.dataset.productId)) return;

    const variantData = window.soleilBadgeData?.[productId]?.[variantId];
    if (!variantData) return;

    const { productBadges, variantBadges } = this.refs;
    if (!productBadges || !variantBadges) return;

    const hasDiscount = Boolean(variantData.discount);
    const hasCustomBadges = Array.isArray(variantData.badges) && variantData.badges.length > 0;
    const hasProductBadges = this.dataset.hasProductBadges === 'true';

    let targetMode = 'empty';
    if (hasDiscount || hasCustomBadges) targetMode = 'variant';
    else if (hasProductBadges) targetMode = 'product';

    // Rebuild the variant container from data — cheap, and keeps the
    // markup authoritative relative to window.soleilBadgeData.
    this.#populateVariant(variantBadges, variantData);

    const applyContainerVisibility = () => {
      productBadges.hidden = targetMode !== 'product';
      variantBadges.hidden = targetMode !== 'variant';
    };

    const willBeHidden = targetMode === 'empty';
    const isCurrentlyHidden = this.classList.contains('soleil-discount-badge--hidden');

    if (willBeHidden === isCurrentlyHidden) {
      applyContainerVisibility();
      return;
    }

    if (willBeHidden) {
      this.classList.add('soleil-discount-badge--hidden');
      applyContainerVisibility();
      return;
    }

    applyContainerVisibility();
    this.classList.remove('soleil-discount-badge--hidden');
  };

  #populateVariant(container, data) {
    container.replaceChildren();
    if (data.discount) {
      container.append(this.#createPill(data.discount));
    }
    if (Array.isArray(data.badges)) {
      for (const b of data.badges) {
        container.append(this.#createPill(b.label, b.bg, b.color));
      }
    }
  }

  #createPill(label, bg, color) {
    const span = document.createElement('span');
    span.className = 'soleil-pill';
    span.style.setProperty('--soleil-pill-bg', bg || '#CC4B2C');
    span.style.setProperty('--soleil-pill-color', color || '#FFFFFF');
    span.textContent = label;
    return span;
  }
}

if (!customElements.get('soleil-discount-badge')) {
  customElements.define('soleil-discount-badge', SoleilDiscountBadge);
}
